// src/constants/Resources.js

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  AuditOutlined,
  BankOutlined,
  CalendarOutlined,
  DollarOutlined,
  DeploymentUnitOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  FundProjectionScreenOutlined,
  FunnelPlotOutlined,
  GlobalOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  ScheduleOutlined,
  SearchOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  SolutionOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { PiToolbox, PiUserFocus } from 'react-icons/pi';
import { MdOutlineLuggage } from 'react-icons/md';
import { BsBoxSeam, BsBuilding } from 'react-icons/bs';
import { TbTruckDelivery } from 'react-icons/tb';
import { GrGroup } from 'react-icons/gr';
import { MdOutlineBadge } from 'react-icons/md';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Resources are identified by UID (unique identifier) that matches systemResourcesPrices.json
// This allows the system to control menu visibility and billing based on subscription resources
//
// `availability` maps each app ID to the environments where the resource is active.
// Example: availability: { applekkus: ['homologation'], appsindico: ['homologation', 'production'] }
// If an app is absent from the map (or the map is commented out), the resource is unavailable for that app.
//
// `requires` lists resource UIDs that are automatically selected (and locked) when this resource is active.
// Dependencies are resolved transitively: if A requires B and B requires C, selecting A adds both B and C.
export const AVAILABLE_RESOURCES = [
  //
  // Granular Resources (per-menu resources for fine-grained access control)
  //
  {
    uid: 'moduleresources_customers',
    label: 'resource_customers',
    description: 'resource_customers_description',
    icon: GrGroup,
    availability: {
      applekkus: ['homologation', 'production'],
      appfunnel: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_suppliers',
    label: 'resource_suppliers',
    description: 'resource_suppliers_description',
    icon: TbTruckDelivery,
    availability: {
      applekkus: ['homologation', 'production'],
    },
  },

  {
    uid: 'moduleresources_collaborators',
    label: 'resource_collaborators',
    description: 'resource_collaborators_description',
    icon: MdOutlineBadge,
    availability: {
      applekkus: ['homologation', 'production'],
      appfunnel: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_companies',
    label: 'resource_companies',
    description: 'resource_companies_description',
    icon: BsBuilding,
    availability: {
      applekkus: ['homologation', 'production'],
      appfunnel: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_products',
    label: 'resource_products',
    description: 'resource_products_description',
    icon: BsBoxSeam,
    moduleUid: 'mod-products-001',
    availability: {
      applekkus: ['homologation', 'production'],
    },
  },

  {
    uid: 'moduleresources_services',
    label: 'resource_services_catalog',
    description: 'resource_services_catalog_description',
    icon: ToolOutlined,
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_toolbox',
    label: 'resource_toolbox',
    description: 'resource_toolbox_description',
    icon: PiToolbox,
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_assets',
    label: 'resource_assets',
    description: 'resource_assets_description',
    icon: ToolOutlined,
    availability: {
      applekkus: ['homologation'],
    },
  },

  //
  // Purchases Resources
  //
  {
    uid: 'moduleresources_purchases',
    label: 'resource_purchases',
    description: 'resource_purchases_description',
    icon: ShoppingCartOutlined,
    requires: ['moduleresources_suppliers', 'moduleresources_products'],
    availability: {
      applekkus: ['homologation', 'production'],
    },
  },

  //
  // Commercial Resources
  //
  {
    uid: 'moduleresources_sales_orders',
    label: 'resource_sales_orders',
    description: 'resource_sales_orders_description',
    icon: FileTextOutlined,
    requires: ['moduleresources_customers', 'moduleresources_products'],
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_pos',
    label: 'resource_pos',
    description: 'resource_pos_description',
    icon: ShopOutlined,
    requires: ['moduleresources_customers', 'moduleresources_products'],
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_contracts',
    label: 'resource_contracts',
    description: 'resource_contracts_description',
    icon: FileProtectOutlined,
    requires: ['moduleresources_customers'],
    availability: {
      applekkus: ['homologation'],
    },
  },

  //
  // Operational Resources
  //
  {
    uid: 'moduleresources_ticket_events',
    label: 'resource_ticket_events',
    description: 'resource_ticket_events_description',
    icon: CalendarOutlined,
    availability: {
      applekkus: ['homologation', 'production'],
    },
  },

  {
    uid: 'moduleresources_inventory',
    label: 'resource_inventory',
    description: 'resource_inventory_description',
    icon: BsBoxSeam,
    requires: ['moduleresources_products'],
    availability: {
      applekkus: ['homologation'],
    },
  },

  //
  // Financial Resources
  //
  {
    uid: 'moduleresources_receivables',
    label: 'resource_receivables',
    description: 'resource_receivables_description',
    icon: DollarOutlined,
    requires: ['moduleresources_customers'],
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_payables',
    label: 'resource_payables',
    description: 'resource_payables_description',
    icon: DollarOutlined,
    requires: ['moduleresources_suppliers'],
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_cashier',
    label: 'resource_cashier',
    description: 'resource_cashier_description',
    icon: BankOutlined,
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_bank_reconciliation',
    label: 'resource_bank_reconciliation',
    description: 'resource_bank_reconciliation_description',
    icon: AuditOutlined,
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_cash_flow',
    label: 'resource_cash_flow',
    description: 'resource_cash_flow_description',
    icon: FundProjectionScreenOutlined,
    availability: {
      applekkus: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_dre',
    label: 'resource_dre',
    description: 'resource_dre_description',
    icon: FileTextOutlined,
    availability: {
      applekkus: ['homologation'],
    },
  },

  //
  // Fiscal Module Resources
  //
  {
    uid: 'moduleresources_quotations',
    label: 'resource_quotations',
    description: 'resource_quotations_description',
    icon: MdOutlineLuggage,
    moduleUid: 'mod-quotations-001',
    availability: {
      appmulti: ['homologation'],
    },
  },

  {
    uid: 'moduleresources_nfe',
    label: 'resource_nfe',
    description: 'resource_nfe_description',
    icon: FileTextOutlined,
    moduleUid: 'mod-fiscal-001',
    requires: ['moduleresources_customers', 'moduleresources_products'],
    availability: {
      applekkus: ['homologation', 'production'],
    },
  },

  {
    uid: 'moduleresources_nfce',
    label: 'resource_nfce',
    description: 'resource_nfce_description',
    icon: ShoppingCartOutlined,
    moduleUid: 'mod-fiscal-001',
    requires: ['moduleresources_customers', 'moduleresources_products'],
    availability: {
      applekkus: ['homologation', 'production'],
    },
  },
  {
    uid: 'moduleresources_nfse',
    label: 'resource_nfse',
    description: 'resource_nfse_description',
    icon: SolutionOutlined,
    moduleUid: 'mod-fiscal-001',
    requires: ['moduleresources_customers', 'moduleresources_services'],
    availability: {
      applekkus: ['homologation'],
      appmulti: ['homologation'],
    },
  },

  //
  // Service Orders (unified: applekkus + appfunnel + appsindico)
  //
  {
    uid: 'moduleresources_service_orders',
    label: 'resource_service_orders',
    description: 'resource_service_orders_description',
    icon: PiToolbox,
    moduleUid: 'mod-services-001',
    requires: ['moduleresources_customers', 'moduleresources_services', 'moduleresources_toolbox'],
    availability: {
      applekkus: ['homologation'],
      // appfunnel: ['homologation'],
      // appsindico: ['homologation'],
    },
  },
  // Sales Resources (products + orders)
  {
    uid: 'moduleresources_sales',
    label: 'quotes_and_orders',
    description: 'resource_sales_description',
    icon: PiToolbox,
    moduleUid: 'mod-sales-001',
    requires: ['moduleresources_customers', 'moduleresources_products'],
    availability: {
      applekkus: ['homologation'],
    },
  },

  //
  // Commercial / Communication Resources
  //

  // Omnichannel Resource
  {
    uid: 'moduleresources_omnichannel',
    label: 'resource_omnichannel',
    description: 'resource_omnichannel_description',
    icon: MessageOutlined,
    moduleUid: 'mod-omnichannel-001',
    availability: {
      applekkus: ['homologation', 'production'],
      appfunnel: ['homologation', 'production'],
      appsindico: ['homologation', 'production'],
    },
  },

  // Leads Resources
  {
    uid: 'moduleresources_leads',
    label: 'resource_leads',
    description: 'resource_leads_description',
    icon: PiUserFocus,
    moduleUid: 'mod-leads-001',
    availability: {
      applekkus: ['homologation', 'production'],
      appfunnel: ['homologation', 'production'],
      appsindico: ['homologation', 'production'],
    },
  },

  // CRM Module Resources
  {
    uid: 'moduleresources_crm',
    label: 'resource_crm',
    description: 'resource_crm_description',
    icon: FunnelPlotOutlined,
    moduleUid: 'mod-crm-001',
    availability: {
      applekkus: ['homologation'],
      appfunnel: ['homologation'],
      appsindico: ['homologation'],
    },
  },

  // VOIP GoTo Resource
  {
    uid: 'moduleresources_voip_goto',
    label: 'resource_voip_goto',
    description: 'resource_voip_goto_description',
    icon: PhoneOutlined,
    isGlobalResource: true,
    availability: {
      applekkus: ['homologation'],
      appfunnel: ['homologation'],
    },
  },

  // Landing Pages Module Resources
  {
    uid: 'moduleresources_landingpages',
    label: 'resource_landing_pages',
    description: 'resource_landing_pages_description',
    icon: GlobalOutlined,
    moduleUid: 'mod-landingpages-001',
    availability: {
      //   applekkus: ['homologation'],
      //   appfunnel: ['homologation'],
      //   appsindico: ['homologation'],
    },
  },

  //
  // Global Resources (subscription-level features)
  //

  // Multi companies
  {
    uid: 'moduleresources_multicompany',
    label: 'multi_company',
    description: 'multi_company_description',
    icon: DeploymentUnitOutlined,
    isGlobalResource: true,
    requires: ['moduleresources_companies'],
    availability: {
      applekkus: ['homologation', 'production'],
      appfunnel: ['homologation'],
      //   appmulti: ['homologation'],
      //   appsindico: ['homologation'],
    },
  },

  // Multi users
  // {
  //   uid: 'moduleresources_multiuser',
  //   label: 'multi_user',
  //   description: 'multi_user_description',
  //   icon: TeamOutlined,
  //   isGlobalResource: true,
  //   requires: ['moduleresources_users'],
  // availability: {
  //   applekkus: ['homologation'],
  //   appmulti: ['homologation'],
  //   appfunnel: ['homologation'],
  //   appsindico: ['homologation'],
  // },
  // },

  //
  // Lookup Module Resources
  //
  {
    uid: 'moduleresources_lookup',
    label: 'lookup_cpf_cnpj',
    description: 'lookup_cpf_cnpj_description',
    icon: SearchOutlined,
    moduleUid: 'mod-lookup-001',
    availability: {
      applekkus: ['homologation'],
      appfunnel: ['homologation'],
      appsindico: ['homologation'],
    },
  },

  //
  // CAUP Resources (ArrendaClick)
  //
  {
    uid: 'moduleresources_caup_dashboard',
    label: 'dashboard',
    description: 'resource_caup_dashboard_description',
    icon: FundProjectionScreenOutlined,
    availability: {
      appcaup: ['homologation'],
    },
  },
  {
    uid: 'moduleresources_caup_contracts',
    label: 'contracts',
    description: 'resource_caup_contracts_description',
    icon: FileProtectOutlined,
    availability: {
      appcaup: ['homologation'],
    },
  },
  {
    uid: 'moduleresources_caup_documentation',
    label: 'documentation',
    description: 'resource_caup_documentation_description',
    icon: AuditOutlined,
    availability: {
      appcaup: ['homologation'],
    },
  },
  {
    uid: 'moduleresources_caup_leads',
    label: 'leads',
    description: 'resource_caup_leads_description',
    icon: FunnelPlotOutlined,
    availability: {
      appcaup: ['homologation'],
    },
  },
  {
    uid: 'moduleresources_caup_calendar',
    label: 'calendar',
    description: 'resource_caup_calendar_description',
    icon: CalendarOutlined,
    availability: {
      appcaup: ['homologation'],
    },
  },
  {
    uid: 'moduleresources_caup_visits',
    label: 'visits',
    description: 'resource_caup_visits_description',
    icon: ScheduleOutlined,
    availability: {
      appcaup: ['homologation'],
    },
  },
  {
    uid: 'moduleresources_caup_templates',
    label: 'templates',
    description: 'resource_caup_templates_description',
    icon: MailOutlined,
    availability: {
      appcaup: ['homologation'],
    },
  },
];

// *******************************************************************************
// DEPENDENCY HELPERS
// *******************************************************************************

/**
 * Resolve all dependencies for a resource UID transitively.
 * If A requires [B] and B requires [C], returns [B, C].
 * Handles circular references via visited set.
 */
export function getAllDependencies(uid, visited = new Set()) {
  if (visited.has(uid)) return [];
  visited.add(uid);

  const resource = AVAILABLE_RESOURCES.find(r => r.uid === uid);
  const directDeps = resource?.requires || [];
  const allDeps = [...directDeps];

  for (const dep of directDeps) {
    allDeps.push(...getAllDependencies(dep, visited));
  }

  return [...new Set(allDeps)];
}

/**
 * Given an array of selected resource UIDs, return a Set of UIDs
 * that are locked (required by at least one selected resource).
 */
export function getLockedDependencies(selectedUids) {
  const locked = new Set();

  for (const uid of selectedUids) {
    const deps = getAllDependencies(uid);
    deps.forEach(d => locked.add(d));
  }

  return locked;
}
